import React from 'react';
import Alert from '@amzn/awsui-components-core/alert';
import Button from '@amzn/awsui-components-core/button';
import FormField from '@amzn/awsui-components-core/form-field';
import Input from '@amzn/awsui-components-core/input';
import Modal from '@amzn/awsui-components-core/modal';
import Select from '@amzn/awsui-components-core/select';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import { AgGridReact } from 'ag-grid-react';
import { StarGridLoading } from 'src/components/star-grid/components/StarGridLoading';
import { useSnapshotManager } from 'src/components/star-grid/hooks/useSnapshotManager';
import { useSnapshotGrid, type SnapshotActionParams } from 'src/components/star-grid/hooks/useSnapshotGrid';
import { useSnapshotTagForm } from 'src/components/star-grid/hooks/useSnapshotTagForm';
import { useStarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import { formatUTCInUserTimeZone } from 'src/utilities/DateTimeUtilities';
import type { SnapshotTagRow } from 'src/components/star-grid/starGridTypes';
import styles from 'src/components/star-grid/views/styles/SnapshotManagerView.module.scss';
import 'src/components/star-grid/views/styles/snapshot-manager-grid.scss';

/**
 * Snapshot Manager — every Iceberg snapshot of the alpha fact, tagged ones first.
 *
 * Tagging names a snapshot in the table itself, labelled the way Prophecy names its FPA Deck Versions.
 */
export const SnapshotManagerView: React.FC = () => {
  const text = useStarGridText();
  const state = useSnapshotManager();
  const form = useSnapshotTagForm(state.prefixes, state.periodFormats);

  // Registered as a named renderer so the column definitions stay plain objects (no closure over
  // component state), which keeps AG Grid from rebuilding every column on each action.
  const components = React.useMemo(
    () => ({
      snapshotActions: ({ data }: SnapshotActionParams) =>
        data && !data.tagged ? (
          <Button variant="inline-link" onClick={() => form.open(data)} data-testid={`snapshot-tag-${data.snapshot_id}`}>
            {text('snapshotTag')}
          </Button>
        ) : null
    }),
    [form, text]
  );
  const grid = useSnapshotGrid({ cellRenderer: 'snapshotActions' });

  if (state.loading && !state.total) {
    return (
      <div className={styles.page} data-testid="snapshot-manager">
        <StarGridLoading label={text('snapshotLoading')} />
      </div>
    );
  }

  return (
    <div className={styles.page} data-testid="snapshot-manager">
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>{text('snapshotManagerTitle')}</h1>
          <p className={styles.subtitle}>
            {text('snapshotManagerHelp')} <code>{state.table}</code>
          </p>
        </div>
        <div className={styles.headerActions}>
          <Input
            value={state.search}
            onChange={({ detail }) => state.setSearch(detail.value)}
            type="search"
            placeholder={text('snapshotSearch')}
            ariaLabel={text('snapshotSearch')}
          />
          <Button iconName="add-plus" variant="primary" disabled={!state.latest} onClick={() => form.open(state.latest)} data-testid="snapshot-new">
            {text('snapshotNew')}
          </Button>
          <Button iconName="refresh" loading={state.loading} onClick={state.refresh} data-testid="snapshot-refresh">
            {text('snapshotRefresh')}
          </Button>
        </div>
      </header>

      {state.error && (
        <Alert type="error" header={text('snapshotLoadFailed')}>
          {state.error}
        </Alert>
      )}

      <div className={styles.grid} data-testid="snapshot-grid">
        <AgGridReact<SnapshotTagRow>
          theme={grid.theme}
          rowData={[...state.snapshots]}
          columnDefs={grid.columns}
          components={components}
          defaultColDef={{ sortable: true, resizable: true, suppressMovable: true, filter: true }}
          getRowId={({ data }) => `${data.tag ?? ''}|${data.snapshot_id}`}
          getRowClass={({ data }) => (data?.tagged ? 'sg-snapshot-tagged' : undefined)}
          rowHeight={34}
          headerHeight={36}
        />
      </div>
      {!state.loading && !state.total && <Alert>{text('snapshotNone')}</Alert>}

      <Modal
        visible={form.visible}
        header={text('snapshotNew')}
        closeAriaLabel={text('studioClose')}
        onDismiss={form.close}
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={form.close}>{text('deckRestoreCancel')}</Button>
            <Button
              variant="primary"
              loading={state.creating}
              disabled={!form.request}
              onClick={async () => {
                if (form.request && (await state.create(form.request))) form.close();
              }}
              data-testid="snapshot-create-confirm"
            >
              {text('snapshotCreate')}
            </Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween size="s">
          <FormField label={text('snapshotForSnapshot')}>
            {form.snapshot?.snapshot_id} {form.snapshot?.committed_at ? `(${formatUTCInUserTimeZone(form.snapshot.committed_at)})` : ''}
          </FormField>
          <FormField label={text('snapshotDeckType')}>
            <Select
              selectedOption={form.deckType ? { value: form.deckType, label: form.deckType } : null}
              options={state.prefixes.map((prefix) => ({ value: prefix, label: prefix }))}
              onChange={({ detail }) => form.setDeckType(detail.selectedOption.value ?? '')}
              ariaLabel={text('snapshotDeckType')}
              data-testid="snapshot-prefix"
            />
          </FormField>
          <FormField
            label={text('snapshotPeriod')}
            constraintText={form.periodFormat}
            errorText={form.periodInvalid ? `${text('snapshotPeriodInvalid')} ${form.periodFormat ?? ''}` : undefined}
          >
            <Input
              value={form.period}
              onChange={({ detail }) => form.setPeriod(detail.value)}
              placeholder={form.periodPlaceholder}
              ariaLabel={text('snapshotPeriod')}
              data-testid="snapshot-period"
            />
          </FormField>
          <FormField label={text('snapshotLabelPreview')}>
            <strong data-testid="snapshot-tag-name">{form.label}</strong>
          </FormField>
          <FormField label={text('snapshotDescription')}>
            <Input
              value={form.description}
              onChange={({ detail }) => form.setDescription(detail.value)}
              placeholder={text('snapshotDescriptionPlaceholder')}
              ariaLabel={text('snapshotDescription')}
              data-testid="snapshot-description"
            />
          </FormField>
        </SpaceBetween>
      </Modal>
    </div>
  );
};

export default SnapshotManagerView;
